/**
 * Dialog para crear o editar un MediaOutlet.
 */
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useOutletKinds } from "@/hooks/use-outlet-kinds";
import { media, type OutletKind, type Coverage, type OutletOut } from "@/lib/api";

const FILE_KINDS = ["pdf", "jpeg", "png", "gif", "webp", "mp3", "wav", "ogg", "mp4", "mov", "avi", "webm", "xlsx", "csv", "docx", "txt", "zip"];

export interface OutletFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outlet?: OutletOut | null;  // null = crear, definido = editar
}

export function OutletFormDialog({ open, onOpenChange, outlet }: OutletFormDialogProps) {
  const qc = useQueryClient();
  const isEditing = !!outlet;
  // Tipos de medio DB-driven (catálogo global gestionado por superadmin)
  const { kinds } = useOutletKinds();
  const defaultAccepts = (kind: string): string[] =>
    kinds.find(k => k.code === kind)?.accepts_formats ?? [];

  const [form, setForm] = useState({
    name: "",
    kind: "" as OutletKind,
    coverage: "" as Coverage | "",
    region_id: "" as string,
    website: "",
    frequency_mhz: "",
    circulation: "",
    accepts_formats: [] as string[],
    notes: "",
    is_active: true,
    // Tarifario
    rate_base_amount: "",
    rate_per_word: "",
    rate_per_second: "",
    rate_per_minute: "",
    rate_per_repetition: "",
    rate_apply_repetitions: false,
    rate_currency: "PEN",
    rate_notes: "",
  });

  useEffect(() => {
    if (outlet) {
      setForm({
        name: outlet.name,
        kind: outlet.kind as OutletKind,
        coverage: (outlet.coverage as Coverage) ?? "",
        region_id: outlet.region_id ? String(outlet.region_id) : "",
        website: outlet.website ?? "",
        frequency_mhz: outlet.frequency_mhz ? String(outlet.frequency_mhz) : "",
        circulation: outlet.circulation ? String(outlet.circulation) : "",
        accepts_formats: outlet.accepts_formats ?? [],
        notes: outlet.notes ?? "",
        is_active: outlet.is_active,
        rate_base_amount: outlet.rate_base_amount ? String(outlet.rate_base_amount) : "",
        rate_per_word: outlet.rate_per_word ? String(outlet.rate_per_word) : "",
        rate_per_second: outlet.rate_per_second ? String(outlet.rate_per_second) : "",
        rate_per_minute: outlet.rate_per_minute ? String(outlet.rate_per_minute) : "",
        rate_per_repetition: outlet.rate_per_repetition ? String(outlet.rate_per_repetition) : "",
        rate_apply_repetitions: outlet.rate_apply_repetitions ?? false,
        rate_currency: outlet.rate_currency ?? "PEN",
        rate_notes: outlet.rate_notes ?? "",
      });
    } else {
      const firstKind = kinds[0]?.code ?? "";
      setForm({
        name: "", kind: firstKind as OutletKind, coverage: "",
        region_id: "", website: "", frequency_mhz: "", circulation: "",
        accepts_formats: defaultAccepts(firstKind),
        notes: "", is_active: true,
        rate_base_amount: "", rate_per_word: "", rate_per_second: "",
        rate_per_minute: "", rate_per_repetition: "",
        rate_apply_repetitions: false, rate_currency: "PEN", rate_notes: "",
      });
    }
  }, [outlet, open]);

  // El catálogo puede llegar después de abrir el dialog: sembrar el default
  // solo si el usuario aún no eligió tipo.
  const firstKindCode = kinds[0]?.code ?? "";
  useEffect(() => {
    if (outlet || !firstKindCode) return;
    setForm(f => f.kind !== "" ? f : {
      ...f,
      kind: firstKindCode as OutletKind,
      accepts_formats: f.accepts_formats.length === 0 ? defaultAccepts(firstKindCode) : f.accepts_formats,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet, open, firstKindCode]);

  function rateBody() {
    return {
      rate_base_amount: form.rate_base_amount ? Number(form.rate_base_amount) : undefined,
      rate_per_word: form.rate_per_word ? Number(form.rate_per_word) : undefined,
      rate_per_second: form.rate_per_second ? Number(form.rate_per_second) : undefined,
      rate_per_minute: form.rate_per_minute ? Number(form.rate_per_minute) : undefined,
      rate_per_repetition: form.rate_per_repetition ? Number(form.rate_per_repetition) : undefined,
      rate_apply_repetitions: form.rate_apply_repetitions,
      rate_currency: form.rate_currency,
      rate_notes: form.rate_notes || undefined,
    };
  }

  const regionsQ = useQuery({
    queryKey: ["media", "geo", "regions"],
    queryFn: () => media.listRegions(),
    staleTime: Infinity,
  });

  const create = useMutation({
    mutationFn: () => media.createOutlet({
      name: form.name,
      kind: form.kind,
      coverage: form.coverage || undefined,
      region_id: form.region_id ? Number(form.region_id) : undefined,
      website: form.website || undefined,
      frequency_mhz: form.frequency_mhz ? Number(form.frequency_mhz) : undefined,
      circulation: form.circulation ? Number(form.circulation) : undefined,
      accepts_formats: form.accepts_formats,
      notes: form.notes || undefined,
      ...rateBody(),
    } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "outlets"] });
      toast.success("Medio creado");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: () => media.patchOutlet(outlet!.id, {
      name: form.name,
      kind: form.kind,
      coverage: form.coverage || undefined,
      region_id: form.region_id ? Number(form.region_id) : undefined,
      website: form.website || undefined,
      frequency_mhz: form.frequency_mhz ? Number(form.frequency_mhz) : undefined,
      circulation: form.circulation ? Number(form.circulation) : undefined,
      accepts_formats: form.accepts_formats,
      notes: form.notes || undefined,
      is_active: form.is_active,
      ...rateBody(),
    } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "outlets"] });
      toast.success("Medio actualizado");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function toggleFormat(k: string) {
    setForm({
      ...form,
      accepts_formats: form.accepts_formats.includes(k)
        ? form.accepts_formats.filter(f => f !== k)
        : [...form.accepts_formats, k],
    });
  }

  function changeKind(newKind: OutletKind) {
    setForm({
      ...form,
      kind: newKind,
      // Si no había formatos elegidos o eran defaults previos, actualiza
      accepts_formats: form.accepts_formats.length === 0 ? defaultAccepts(newKind) : form.accepts_formats,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? `Editar medio: ${outlet.name}` : "Nuevo medio"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="El Peruano" />
            </div>
            <div>
              <Label className="text-xs">Tipo de medio *</Label>
              <Select value={form.kind} onValueChange={(v) => changeKind(v as OutletKind)}>
                <SelectTrigger><SelectValue placeholder="Seleccionar tipo..." /></SelectTrigger>
                <SelectContent>
                  {kinds.map(k => (
                    <SelectItem key={k.code} value={k.code}>{k.name}</SelectItem>
                  ))}
                  {/* Tipo desactivado del catálogo pero aún asignado al medio en edición */}
                  {form.kind && !kinds.some(k => k.code === form.kind) && (
                    <SelectItem value={form.kind}>{form.kind}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cobertura</Label>
              <Select value={form.coverage} onValueChange={(v) => setForm({ ...form, coverage: v as Coverage })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nacional">Nacional</SelectItem>
                  <SelectItem value="regional">Regional</SelectItem>
                  <SelectItem value="local">Local</SelectItem>
                  <SelectItem value="zona">Zona</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Región (opcional)</Label>
              <Select value={form.region_id} onValueChange={(v) => setForm({ ...form, region_id: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {(regionsQ.data ?? []).map(r => (
                    <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sitio web</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
            </div>
            <div>
              <Label className="text-xs">Frecuencia MHz (radio)</Label>
              <Input type="number" step="0.1" value={form.frequency_mhz}
                onChange={(e) => setForm({ ...form, frequency_mhz: e.target.value })}
                placeholder="95.5" />
            </div>
            <div>
              <Label className="text-xs">Tiraje (prensa)</Label>
              <Input type="number" value={form.circulation}
                onChange={(e) => setForm({ ...form, circulation: e.target.value })} />
            </div>
          </div>

          <div>
            <Label className="text-xs">Formatos aceptados *</Label>
            <p className="text-[10px] text-ink-400 mb-2">Las evidencias subidas a este medio se validarán contra estos formatos.</p>
            <div className="flex flex-wrap gap-2">
              {FILE_KINDS.map(k => {
                const on = form.accepts_formats.includes(k);
                return (
                  <button
                    key={k} type="button"
                    onClick={() => toggleFormat(k)}
                    className={`px-2 py-1 rounded text-xs font-mono uppercase border transition-colors ${
                      on
                        ? "bg-brand-50 dark:bg-brand-900/40 text-brand-700 border-brand-300"
                        : "bg-ink-50 dark:bg-ink-800 text-ink-500 border-ink-200 hover:border-ink-400"
                    }`}
                  >
                    {k}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs">Notas</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          {isEditing && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <span className="text-sm">Medio activo</span>
            </label>
          )}

          {/* ============ Tarifario ============ */}
          <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">💰 Tarifario (rate card)</Label>
              <Input
                value={form.rate_currency}
                onChange={(e) => setForm({ ...form, rate_currency: e.target.value.toUpperCase().slice(0, 3) })}
                maxLength={3}
                className="w-20 h-8 text-xs font-mono uppercase"
              />
            </div>
            <p className="text-[10px] text-ink-400 mb-3">
              Opcional. Si se setea, los placements en este medio auto-calculan costo.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Costo base</Label>
                <Input type="number" step="0.01" min="0"
                  value={form.rate_base_amount}
                  onChange={(e) => setForm({ ...form, rate_base_amount: e.target.value })}
                  placeholder="0.00" />
                <p className="text-[10px] text-ink-400 mt-0.5">Fijo por inserción</p>
              </div>
              <div>
                <Label className="text-xs">Por palabra</Label>
                <Input type="number" step="0.0001" min="0"
                  value={form.rate_per_word}
                  onChange={(e) => setForm({ ...form, rate_per_word: e.target.value })}
                  placeholder="0.0000" />
                <p className="text-[10px] text-ink-400 mt-0.5">Prensa escrita</p>
              </div>
              <div>
                <Label className="text-xs">Por segundo</Label>
                <Input type="number" step="0.0001" min="0"
                  value={form.rate_per_second}
                  onChange={(e) => setForm({ ...form, rate_per_second: e.target.value })}
                  placeholder="0.0000" />
                <p className="text-[10px] text-ink-400 mt-0.5">Radio / TV (spot)</p>
              </div>
              <div>
                <Label className="text-xs">Por minuto</Label>
                <Input type="number" step="0.0001" min="0"
                  value={form.rate_per_minute}
                  onChange={(e) => setForm({ ...form, rate_per_minute: e.target.value })}
                  placeholder="0.0000" />
                <p className="text-[10px] text-ink-400 mt-0.5">Alt. a segundos</p>
              </div>
              <div>
                <Label className="text-xs">Por repetición</Label>
                <Input type="number" step="0.0001" min="0"
                  value={form.rate_per_repetition}
                  onChange={(e) => setForm({ ...form, rate_per_repetition: e.target.value })}
                  placeholder="0.0000" />
                <p className="text-[10px] text-ink-400 mt-0.5">Costo por emisión</p>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.rate_apply_repetitions}
                    onChange={(e) => setForm({ ...form, rate_apply_repetitions: e.target.checked })}
                  />
                  <span className="text-xs">Multiplicar subtotal × repeticiones</span>
                </label>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Notas tarifario</Label>
              <Textarea
                rows={2}
                value={form.rate_notes}
                onChange={(e) => setForm({ ...form, rate_notes: e.target.value })}
                placeholder="Ej: Tarifa válida 2026. Descuentos por volumen 5%+. Sin IGV."
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={() => isEditing ? update.mutate() : create.mutate()}
            disabled={!form.name || !form.kind || form.accepts_formats.length === 0 || create.isPending || update.isPending}
          >
            {isEditing ? "Guardar cambios" : "Crear medio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
